import { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Select, { SingleValue } from 'react-select';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';

import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrowdown.svg';
import Button from 'common/button/button';
import MonthInput from 'common/month-input/month-input';
import ToggleButton from 'components/toggleButton/ToggleButton';
import { IPaystubSendHistoryItem, ISendHistoryItem, IStatistics } from 'types/salary';
import CommonModal from 'common/CommonModal';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';
import { addComma } from 'constants/utils';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedDate } from 'state/salarySlice';
import { IRootState } from '~/state/store';
import {
  ICalculatePaystubDeductionRes,
  IGetPaystubPayInfoRes,
  IPaystubEmployeeItem,
  ISavePaystubReq,
  IGetFixedDataRes,
} from '~/types/paystub';
import PaystubPaymentTable from 'components/paystub/paystub-payment-table';
import PaystubPaymentCustomTable from 'components/paystub/paystub-payment-custom-table';
import PaystubPaymentInfo from 'components/paystub/paystub-payment-info';
import PaystubDeductionTable from 'components/paystub/paystub-deduction-table';
import PaystubDeductionCustomTable from 'components/paystub/paystub-deduction-custom-table';
import PaystubSendHistoryTable from 'components/paystub/paystub-send-history-table';
import PaystubStatistics from 'components/paystub/paystub-statistics';
import PaystubPreview from './paystub-preview';

const payTypeOptions: { value: string; label: string }[] = [
  { value: '시급', label: '시급' },
  { value: '일급', label: '일급' },
  { value: '월급', label: '월급' },
];

const PaystubSendAndWrite = () => {
  const dispatch = useDispatch();
  const { selectedDate } = useSelector((state: IRootState) => state.salary);
  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();
  const navigate = useNavigate();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [isPreview, setIsPreview] = useState(false);
  const [date, setDate] = useState<Date>(new Date(selectedDate));
  // AG Grid 셀 렌더러가 오래된 date를 참조하지 않도록 최신 값을 보관
  const dateRef = useRef<Date>(date);
  dateRef.current = date;
  // 전월 대비 급여 통계
  const [payStatistics, setPayStatistics] = useState<IStatistics>({
    show: false,
    prevEmps: 0,
    prevPayed: 0,
    prevDeductions: 0,
    prevRealPayed: 0,
    monthEmps: 0,
    monthPayed: 0,
    monthDeductions: 0,
    monthRealPayed: 0,
  });
  const [selectedEmpInfo, setSelectedEmpInfo] = useState<IPaystubEmployeeItem | null>(null); // 직원목록에서 클릭한 직원 정보(급여표시)
  const [empList, setEmpList] = useState<IPaystubEmployeeItem[]>([]); // 직원 목록
  const [selectedEmps, setSelectedEmps] = useState<IPaystubEmployeeItem[]>([]); // 체크박스로 선택된 직원들

  const [payment, setPayment] = useState<IGetPaystubPayInfoRes['payment']>({
    id: 0,
    EMP_SEQ: Number(selectedEmpInfo?.EMP_SEQ),
    payment_month: moment(date).format('YYYY-MM'),
    salary_pay: 0,
    pay_type: '시급',
    work_day: 0,
    work_day2: 0,
    work_time_hour: 0,
    work_time_minute: 0,
    week_time_hour: 0,
    week_time_minute: 0,
    over_time_hour: 0,
    over_time_minute: 0,
    night_work_time_hour: 0,
    night_work_time_minute: 0,
    holiday_work_time_hour: 0,
    holiday_work_time_minute: 0,
    pay2_flag: false,
    salary_pay2: 0,
    work_time2_hour: 0,
    work_time2_minute: 0,
    week_time2_hour: 0,
    week_time2_minute: 0,
    over_time2_hour: 0,
    over_time2_minute: 0,
    night_work_time2_hour: 0,
    night_work_time2_minute: 0,
    holiday_work_time2_hour: 0,
    holiday_work_time2_minute: 0,
    payment_items: [
      { name: '식대(비과세)', value: 0, type: 'NON_TAXABLE' },
      { name: '기타(비과세)', value: 0, type: 'NON_TAXABLE' },
    ],
    total_pay: 0,
    deduction_items: [],
    create_dt: '',
    update_dt: '',
  }); // 지급 항목
  const [tempPayment, setTempPayment] = useState<IGetPaystubPayInfoRes['payment']>(payment);
  const [isCalculated, setIsCalculated] = useState(false); // 공제 계산 여부
  const [previewInfo, setPreviewInfo] = useState<IGetFixedDataRes['resultData']>([]);

  const [deduction, setDeduction] = useState<ICalculatePaystubDeductionRes>({
    basic_items: [],
    payment_items: [],
    deduction_items: [],
    total_pay: '',
    empInfo: {
      EMP_NAME: '',
      BIRTHDATE: '',
      STORE_NAME: '',
      PAYMENT_TYPE: '',
      TAX_TYPE: '',
      PAY_DAY: '',
      CALCULATE_PERIOD: '',
      payment_amount: '',
      send_time: '',
    },
  }); // 공제

  const [sendHistoryData, setSendHistoryData] = useState<{
    modalShow: boolean; // 급여명세서 전송 내역 모달 표시 여부
    memberIndex: number; // 선택한 직원명
    empName: string; // 선택한 직원명
    data: IPaystubSendHistoryItem[]; // 전송내역 데이터
  }>({
    modalShow: false,
    memberIndex: 0,
    empName: '',
    data: [],
  });

  // AG Grid 기본 컬럼 설정
  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      cellStyle: {
        textAlign: 'center',
      },
    }),
    [],
  );

  // 직원 목록 컬럼 정의
  const empColumns: (ColDef | ColGroupDef)[] = useMemo(
    () => [
      {
        headerName: '',
        field: 'isChecked',
        checkboxSelection: true,
        headerCheckboxSelection: true,
        width: 50,
      },
      {
        field: 'NUMBER',
        headerName: 'NO.',
        width: 80,
        valueGetter: (params) => (params.node?.rowIndex != null ? params.node.rowIndex + 1 : ''),
      },
      {
        field: 'EMP_NAME',
        headerName: '이름',
        flex: 1,
        autoHeight: true,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        } as any,
        onCellClicked: (params: any) => {
          selectEmp(params.data);
        },
        cellRenderer: ({ data }: { data: IPaystubEmployeeItem }) => {
          const NameCell = () => {
            if (data?.END) {
              return (
                <div className="flex h-full w-full flex-col items-center justify-center py-2">
                  <span>{data.EMP_NAME}</span>
                  <span className="text-[0.7rem] text-[#777]">{`${data.END} 퇴사 예정`}</span>
                </div>
              );
            }
            return (
              <div className="flex h-full w-full items-center justify-center">
                <span>{data.EMP_NAME}</span>
              </div>
            );
          };
          return <NameCell />;
        },
      },
      {
        field: 'MSG_COUNT',
        headerName: '전송수',
        width: 90,
      },
      {
        field: 'LAST_PUSH_DATE',
        headerName: '최근발송',
        flex: 1,
        cellStyle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        cellRenderer: ({ value, data }: { value: string; data: IPaystubEmployeeItem }) => {
          const DateCell = () => {
            const [isHovered, setIsHovered] = useState(false);
            const formattedDate = moment(value).isValid() ? moment(value).format('YYYY.MM.DD HH:mm') : value;

            const handleClick = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();

              if (data.MSG_COUNT === 0) {
                setModalState({
                  isModalShow: true,
                  title: '발송 내역 없음',
                  desc: '발송 내역이 없습니다.',
                });
                return;
              }

              fetchSendHistoryData(data.EMP_SEQ, data.EMP_NAME);
            };

            return (
              <div
                className="flex h-full w-full cursor-pointer items-center justify-center"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleClick}
                onMouseDown={(e) => e.stopPropagation()}>
                <span className={isHovered ? 'text-[#888888]' : ''}>{isHovered ? '발송내역보기' : formattedDate}</span>
              </div>
            );
          };
          return <DateCell />;
        },
      },
    ],
    [selectedEmpInfo],
  );

  const enterDetailPayScreen = (id: string) => {
    navigate(`/paystub-owner-preview?id=${id}`);
  };

  // 급여명세서 직원목록 조회 [NEW]
  const fetchPaystubEmployeeList = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/paystubEmpList`, {
        YEAR: moment(date).year().toString(),
        MONTH: (moment(date).month() + 1).toString().padStart(2, '0'),
        SORT_TYPE: 'ASC',
      });

      if (data.message === 'SUCCESS') {
        const { empList } = data.resultData;

        if (empList.length > 0) {
          setEmpList(
            empList.map((i: IPaystubEmployeeItem, index: number) => {
              return {
                ...i,
                NUMBER: index + 1,
              };
            }),
          );
        }
        if (selectedEmpInfo === null) {
          setSelectedEmpInfo(
            empList.map((i: IPaystubEmployeeItem, index: number) => {
              return {
                ...i,
                NUMBER: index + 1,
              };
            })[0],
          );
        }
      } else {
        throw new Error('/auth/getPaystubMember');
      }
    } catch (err) {
      checkErrorLog(err, 'employee-list-for-payroll /auth/getPaystubMember');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 지급 항목 패칭 [NEW]
  const fetchPaystubPayInfo = async (empSeq: number, date: Date) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getPaystubPayInfo`, {
        EMP_SEQ: empSeq,
        payment_month: moment(date).format('YYYY-MM'),
      });

      if (data.message === 'SUCCESS') {
        setIsCalculated(true);
        setPayment({
          ...data.resultData.payment,
          EMP_SEQ: empSeq,
        });
        setTempPayment({
          ...data.resultData.payment,
          EMP_SEQ: empSeq,
        });
        setDeduction({
          ...data.resultData.deduction,
        });
      } else {
        const { data: selectBoxData }: AxiosResponse['data'] = await webApiNode('get', `/paystub/getSelectboxList`, {});

        if (selectBoxData.message === 'SUCCESS') {
          setIsCalculated(false);
          setPayment({
            EMP_SEQ: empSeq,
            payment_items: selectBoxData.resultData.map((item: { name: string }) => ({
              name: item.name,
              value: 0,
              type: 'NON_TAXABLE',
            })) as {
              name: string;
              value: number;
              type: 'NON_TAXABLE' | 'TAXABLE';
            }[],
            id: 0,
            payment_month: moment(date).format('YYYY-MM'),
            salary_pay: 0,
            pay_type: '시급',
            work_day: 0,
            work_day2: 0,
            work_time_hour: 0,
            work_time_minute: 0,
            week_time_hour: 0,
            week_time_minute: 0,
            over_time_hour: 0,
            over_time_minute: 0,
            night_work_time_hour: 0,
            night_work_time_minute: 0,
            holiday_work_time_hour: 0,
            holiday_work_time_minute: 0,
            pay2_flag: false,
            salary_pay2: 0,
            work_time2_hour: 0,
            work_time2_minute: 0,
            week_time2_hour: 0,
            week_time2_minute: 0,
            over_time2_hour: 0,
            over_time2_minute: 0,
            night_work_time2_hour: 0,
            night_work_time2_minute: 0,
            holiday_work_time2_hour: 0,
            holiday_work_time2_minute: 0,
            total_pay: 0,
            deduction_items: [],
            create_dt: '',
            update_dt: '',
          });
          setDeduction({
            ...deduction,
            basic_items: [],
            payment_items: [],
            deduction_items: [],
            total_pay: '',
            empInfo: {
              EMP_NAME: '',
              BIRTHDATE: '',
              STORE_NAME: '',
              PAYMENT_TYPE: '',
              TAX_TYPE: '',
              PAY_DAY: '',
              CALCULATE_PERIOD: '',
              payment_amount: '',
              send_time: '',
            },
          });
        }
      }
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 지난달 급여 불러오기 패칭 [NEW]
  const fetchPrevMonthPayInfo = async (empSeq: number, date: Date) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getPaystubPayInfo`, {
        EMP_SEQ: empSeq,
        payment_month: moment(date).format('YYYY-MM'),
      });

      setIsCalculated(true);
      setPayment(data.resultData.payment);
      setTempPayment(data.resultData.payment);
      setDeduction(data.resultData.deduction);
    } catch (err) {
      setModalState({
        isModalShow: true,
        title: '지난달 급여 불러오기 실패',
        desc: '지난달 급여가 없어요',
      });
      // checkErrorLog(err, 'paystub/getPaystubPayInfo');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 초기화 [NEW]
  const handleResetPayInfo = (payType: string) => {
    setIsCalculated(false);
    const initialPayment: IGetPaystubPayInfoRes['payment'] = {
      id: 0,
      EMP_SEQ: Number(selectedEmpInfo?.EMP_SEQ),
      payment_month: moment(date).format('YYYY-MM'),
      salary_pay: 0,
      pay_type: payType,
      work_day: 0,
      work_day2: 0,
      work_time_hour: 0,
      work_time_minute: 0,
      week_time_hour: 0,
      week_time_minute: 0,
      over_time_hour: 0,
      over_time_minute: 0,
      night_work_time_hour: 0,
      night_work_time_minute: 0,
      holiday_work_time_hour: 0,
      holiday_work_time_minute: 0,
      pay2_flag: false,
      salary_pay2: 0,
      work_time2_hour: 0,
      work_time2_minute: 0,
      week_time2_hour: 0,
      week_time2_minute: 0,
      over_time2_hour: 0,
      over_time2_minute: 0,
      night_work_time2_hour: 0,
      night_work_time2_minute: 0,
      holiday_work_time2_hour: 0,
      holiday_work_time2_minute: 0,
      payment_items: [
        { name: '식대(비과세)', value: 0, type: 'NON_TAXABLE' as const },
        { name: '기타(비과세)', value: 0, type: 'NON_TAXABLE' as const },
      ],
      total_pay: 0,
      deduction_items: [],
      create_dt: '',
      update_dt: '',
    };

    setPayment(initialPayment);
    setTempPayment(initialPayment);

    setDeduction({
      basic_items: [],
      payment_items: [],
      deduction_items: [],
      total_pay: '',
      empInfo: {
        EMP_NAME: '',
        BIRTHDATE: '',
        STORE_NAME: '',
        PAYMENT_TYPE: '',
        TAX_TYPE: '',
        PAY_DAY: '',
        CALCULATE_PERIOD: '',
        payment_amount: '',
        send_time: '',
      },
    });
  };

  // 출퇴근 기반 급여 정보 가져오기 [NEW]
  const fetchEmpSchedulePayInfo = async (empSeq: number, date: Date) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getEmpSchedulePayInfo`, {
        YEAR: moment(date).format('YYYY'),
        MONTH: moment(date).format('MM'),
        EMP_SEQ: empSeq,
      });

      setIsCalculated(true);
      setPayment(data.resultData.payment);
      setTempPayment(data.resultData.payment);
      setDeduction(data.resultData.deduction);
    } catch (err) {
      checkErrorLog(err, 'paystub/getEmpSchedulePayInfo');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 고정 데이터 패칭 [NEW]
  const fetchFixedData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('get', `/paystub/getFixedData`);

      if (data.message === 'SUCCESS') {
        setPreviewInfo(data.resultData);
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/getFixedData');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 계산 API [NEW]
  const calculatePaystub = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/calculatePaystubDeduction`, {
        EMP_SEQ: payment.EMP_SEQ,
        pay_type: payment.pay_type === '시급' ? '0' : payment.pay_type === '일급' ? '1' : '2',
        payment_month: payment.payment_month,
        salary_pay: payment.salary_pay,
        work_day: payment.work_day,
        work_day2: payment.work_day2,
        work_time_hour: payment.work_time_hour,
        work_time_minute: payment.work_time_minute,
        week_time_hour: payment.week_time_hour,
        week_time_minute: payment.week_time_minute,
        over_time_hour: payment.over_time_hour,
        over_time_minute: payment.over_time_minute,
        night_work_time_hour: payment.night_work_time_hour,
        night_work_time_minute: payment.night_work_time_minute,
        holiday_work_time_hour: payment.holiday_work_time_hour,
        holiday_work_time_minute: payment.holiday_work_time_minute,
        pay2_flag: payment.pay2_flag,
        salary_pay2: payment.salary_pay2,
        work_time2_hour: payment.work_time2_hour,
        work_time2_minute: payment.work_time2_minute,
        week_time2_hour: payment.week_time2_hour,
        week_time2_minute: payment.week_time2_minute,
        over_time2_hour: payment.over_time2_hour,
        over_time2_minute: payment.over_time2_minute,
        night_work_time2_hour: payment.night_work_time2_hour,
        night_work_time2_minute: payment.night_work_time2_minute,
        holiday_work_time2_hour: payment.holiday_work_time2_hour,
        holiday_work_time2_minute: payment.holiday_work_time2_minute,
        payment_items: payment.payment_items.map((item) => ({
          name: item.name,
          value: item.value,
          type: item.type,
        })),
      });

      if (data.message === 'SUCCESS') {
        setIsCalculated(true);
        setTempPayment(payment); // 임시 저장용  ( 저장하기 시 임시와 다른 경우 저장 X )

        // 계산하기 시 추가된 공제항목 유지
        const customDeductionItems = deduction.deduction_items.filter((item) => item.edit_type === 'CUSTOM');

        setDeduction({
          ...data.resultData,
          deduction_items: [...data.resultData.deduction_items, ...customDeductionItems],
        });
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/calculatePaystubDeduction');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 전송 내역 데이터 패칭 [NEW]
  const fetchSendHistoryData = async (empSeq: number, empName: string) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getPaystubHistory`, {
        EMP_SEQ: empSeq,
        payment_month: moment(dateRef.current).format('YYYY-MM'),
      });

      setSendHistoryData({
        modalShow: true,
        memberIndex: empSeq,
        empName: empName,
        data: data.resultData.map((item: IPaystubSendHistoryItem) => ({
          id: item.id,
          create_dt: item.create_dt,
        })),
      });
    } catch (err) {
      checkErrorLog(err, 'paystub/getPaystubHistory');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 전송 [NEW]
  const sendPaystub = async (empSeqs: number[]) => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/sendPaystub`, {
        EMP_SEQ: empSeqs,
        payment_month: moment(date).format('YYYY-MM'),
      });
      if (data.message === 'SUCCESS') {
        setModalState({
          isModalShow: true,
          title: '급여명세서 전송 완료',
          desc: '급여명세서 전송이 완료되었어요',
        });
        fetchPaystubEmployeeList();
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'paystub/sendPaystub');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 통계 데이터 패칭
  const fetchAccumulatedStatisticsData = async () => {
    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/getAccumulatedStatisticsData`, {
        YEAR: moment(date).format('YYYY'),
        MONTH: moment(date).format('MM'),
      });

      if (data.message === 'SUCCESS') {
        const {
          THIS_WEEK_WORKER,
          THIS_WEEK_PAY,
          THIS_WEEK_DEDUC,
          THIS_WEEK_ACTUAL_PAY,
          LAST_WEEK_WORKER,
          LAST_WEEK_PAY,
          LAST_WEEK_DEDUC,
          LAST_WEEK_ACTUAL_PAY,
          WORKER_DIFF,
          PAY_DIFF,
          DEDUC_DIFF,
          ACTUAL_PAY_DIFF,
        } = data.resultData;

        setPayStatistics({
          show: true,
          prevEmps: LAST_WEEK_WORKER,
          prevPayed: LAST_WEEK_PAY,
          prevDeductions: LAST_WEEK_DEDUC,
          prevRealPayed: LAST_WEEK_ACTUAL_PAY,
          monthEmps: THIS_WEEK_WORKER,
          monthPayed: THIS_WEEK_PAY,
          monthDeductions: THIS_WEEK_DEDUC,
          monthRealPayed: THIS_WEEK_ACTUAL_PAY,
        });
      } else {
        throw new Error(data.resultMsg);
      }
    } catch (err) {
      checkErrorLog(err, 'BusinessEstablishmentChoice /auth/getStatisticsV2');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 급여명세서 저장 핸들러
  const savePaystub = async () => {
    if (!tempPayment) {
      setModalState({
        isModalShow: true,
        title: '변경된 지급항목이 있어요',
        desc: '계산하기를 다시 클릭해야\n변경된 지급항목이 적용돼요\n계산하기 클릭 시 추가된 공제항목은\n저장되지 않아요',
      });
      return;
    }

    // 지급항목이 변경되었는지 확인 (비교할 필드만 추출)
    const getCompareFields = (p: typeof payment) => ({
      salary_pay: p.salary_pay,
      work_day: p.work_day,
      work_day2: p.work_day2,
      work_time_hour: p.work_time_hour,
      work_time_minute: p.work_time_minute,
      week_time_hour: p.week_time_hour,
      week_time_minute: p.week_time_minute,
      over_time_hour: p.over_time_hour,
      over_time_minute: p.over_time_minute,
      night_work_time_hour: p.night_work_time_hour,
      night_work_time_minute: p.night_work_time_minute,
      holiday_work_time_hour: p.holiday_work_time_hour,
      holiday_work_time_minute: p.holiday_work_time_minute,
      pay2_flag: p.pay2_flag,
      salary_pay2: p.salary_pay2,
      work_time2_hour: p.work_time2_hour,
      work_time2_minute: p.work_time2_minute,
      week_time2_hour: p.week_time2_hour,
      week_time2_minute: p.week_time2_minute,
      over_time2_hour: p.over_time2_hour,
      over_time2_minute: p.over_time2_minute,
      night_work_time2_hour: p.night_work_time2_hour,
      night_work_time2_minute: p.night_work_time2_minute,
      holiday_work_time2_hour: p.holiday_work_time2_hour,
      holiday_work_time2_minute: p.holiday_work_time2_minute,
      payment_items: p.payment_items,
    });

    const isPaymentChanged =
      JSON.stringify(getCompareFields(payment)) !== JSON.stringify(getCompareFields(tempPayment));

    if (isPaymentChanged) {
      setModalState({
        isModalShow: true,
        title: '변경된 지급내역이 있어요',
        desc: '계산하기를 다시 클릭해야\n변경된 지급항목이 적용돼요\n계산하기 클릭 시 추가된 공제항목은\n저장되지 않아요',
      });
      return;
    }

    if (payment.pay_type === '시급') {
      if (
        !payment.salary_pay ||
        payment.salary_pay === 0 ||
        (payment.work_time_hour === 0 && payment.work_time_minute === 0)
      ) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '시급 또는 근로시간을 입력해주세요',
        });
        return;
      }

      // 1-1. 기본급2 설정한 경우 시급2과 근로시간2 필수
      if (payment.pay2_flag) {
        if (
          !payment.salary_pay2 ||
          payment.salary_pay2 === 0 ||
          (payment.work_time2_hour === 0 && payment.work_time2_minute === 0)
        ) {
          setModalState({
            isModalShow: true,
            title: '입력 오류',
            desc: '시급 또는 근로시간을 입력해주세요',
          });
          return;
        }
      }
    }

    // 2. 일급인 경우 일과 일급 필수
    if (payment.pay_type === '일급') {
      if (!payment.work_day || payment.work_day === 0 || !payment.salary_pay || payment.salary_pay === 0) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '일급 또는 근무일을 입력해주세요',
        });
        return;
      }
    }

    // 3. 월급인 경우 월급 필수
    if (payment.pay_type === '월급') {
      if (!payment.salary_pay || payment.salary_pay === 0) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '월급을 입력해주세요',
        });
        return;
      }
    }

    const { id, update_dt, create_dt, ...restPayment } = payment;
    const req: ISavePaystubReq = {
      ...restPayment,
      payment_month: moment(date).format('YYYY-MM'),
      pay_type: payment.pay_type === '시급' ? '0' : payment.pay_type === '일급' ? '1' : '2',
      deduction_items: deduction.deduction_items.map((item) => ({
        name: item.name,
        value: Number(item.value.replace(/[^0-9]/g, '')),
        edit_type: item.edit_type,
        formula: item.formula,
      })),
      total_pay: Number(deduction.total_pay.replace(/[^0-9]/g, '')),
      payment_items: payment.payment_items.map((item) => ({
        name: item.name,
        value: Number(item.value),
        type: item.type,
      })),
    };

    try {
      setLoadingState({ isFetchLoading: true });
      const { data }: AxiosResponse['data'] = await webApiNode('post', `/paystub/savePaystub`, req as ISavePaystubReq);
    } catch (err) {
      checkErrorLog(err, 'paystub/savePaystub');
    } finally {
      setLoadingState({ isFetchLoading: false });
    }
  };

  // 통계 모달 토글
  const toggleStatisticModal = () => {
    setPayStatistics((prev) => ({ ...prev, show: !prev.show }));
  };

  // 카카오톡 전송 버튼 클릭 시 데이터 검증
  const clickSendKakao = () => {
    if (selectedEmps.length === 0) {
      return setModalState({
        isModalShow: true,
        title: '급여명세서 전송',
        desc: `등록된 직원 목록에서 전송할 직원의 체크박스에 체크해 주세요.`,
      });
    } else {
      return setModalState({
        isModalShow: true,
        title: '급여명세서 전송',
        desc: `직원 목록에서 체크표시 된 ${
          selectedEmps.length
        }명의 직원들에게 급여명세서를 전송합니다.\n\n${selectedEmps.map((i) => i.EMP_NAME).join(' / ')}`,
        okBtnText: '확인',
        okCallback: () => sendPaystub(selectedEmps.map((i) => i.EMP_SEQ)),
      });
    }
  };

  // 급여명세서 전송 내역 모달 표시 토글
  const toggleSendHistoryModalVisible = () => {
    setSendHistoryData((prevData) => {
      return {
        ...prevData,
        modalShow: !prevData.modalShow,
      };
    });
  };

  // 직원목록에서 직원 선택
  const selectEmp = (empData: IPaystubEmployeeItem) => {
    setSelectedEmpInfo(empData);
  };

  // 출퇴근 기반 급여정보 불러오기
  const confirmGetRealPayByCommute = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `출퇴근 기반 급여 불러오기`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `출퇴근 기반 급여 불러오기`,
        desc: `- 직원의 출퇴근 기반 데이터(근무시간,근무일수,시급,일급,월급 등)를 불러옵니다.\n\n -선택한 직원이 사업주로부터 초대를 받고 사업장 출퇴근을 진행한 경우에만 불러올 수 있습니다.\n\n -정산 탭의 [급여대장] 데이터와 동일하며, [급여대장] 시급직의 경우 일별 급여액(출퇴근 시간 x 시급)을 합산하여 계산합니다. 따라서 원단위가 절삭되어 현재 선택된 월의 [급여명세서] 기본급과 [급여대장] 기본급 금액의 차이가 발생할 수 있습니다.\n\n -데이터를 불러온 후 [공제 계산하기] 버튼을 눌러 급여를 계산하고 저장해 주세요.`,
        okBtnText: '확인',
        okCallback: () => fetchEmpSchedulePayInfo(selectedEmpInfo?.EMP_SEQ, date),
      });
    }
  };

  // 월 변경
  const handleCalendarInput = (date: Date) => {
    setDate(date);
    dispatch(setSelectedDate(date.toISOString()));
  };

  // 급여유형 변경
  const handlePayType = (
    newValue: SingleValue<{
      value: string;
      label: string;
    }>,
  ) => {
    if (newValue) {
      setPayment({ ...payment, pay_type: newValue.value });
    }
  };

  const colorStyles = {
    control: (styles: any) => ({
      ...styles,
      backgroundColor: 'white',
      border: '1px solid #e9e9e9',
      borderRadius: '8px',
      width: '100%',
      minHeight: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      boxShadow: 'none',
      '&:hover': {
        border: '1px solid #e9e9e9',
      },
    }),
    valueContainer: (styles: any) => ({
      ...styles,
      height: '40px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (styles: any) => ({
      ...styles,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (styles: any) => ({
      ...styles,
      height: '40px',
    }),
    multiValueRemove: (base: any) => ({ ...base, display: 'none' }),
  };

  // 시급-기본급2 사용여부 토글
  const toggleUseSecondDefaultPay = (checked: boolean) => {
    setPayment((prev) => ({ ...prev, pay2_flag: checked }));
  };

  // 계산하기 핸들러
  const handleCalculate = async () => {
    // 유효성 처리
    // 1. 시급인 경우 시급과 근로시간 필수
    if (payment.pay_type === '시급') {
      if (
        !payment.salary_pay ||
        payment.salary_pay === 0 ||
        (payment.work_time_hour === 0 && payment.work_time_minute === 0)
      ) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '시급 또는 근로시간을 입력해주세요',
        });
        return;
      }

      // 1-1. 기본급2 설정한 경우 시급2과 근로시간2 필수
      if (payment.pay2_flag) {
        if (
          !payment.salary_pay2 ||
          payment.salary_pay2 === 0 ||
          (payment.work_time2_hour === 0 && payment.work_time2_minute === 0)
        ) {
          setModalState({
            isModalShow: true,
            title: '입력 오류',
            desc: '시급 또는 근로시간을 입력해주세요',
          });
          return;
        }
      }
    }

    // 2. 일급인 경우 일과 일급 필수
    if (payment.pay_type === '일급') {
      if (!payment.work_day || payment.work_day === 0 || !payment.salary_pay || payment.salary_pay === 0) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '일급 또는 근무일을 입력해주세요',
        });
        return;
      }
    }

    // 3. 월급인 경우 월급 필수
    if (payment.pay_type === '월급') {
      if (!payment.salary_pay || payment.salary_pay === 0) {
        setModalState({
          isModalShow: true,
          title: '입력 오류',
          desc: '월급을 입력해주세요',
        });
        return;
      }
    }

    await calculatePaystub();
  };

  // 지난달 급여 불러오기 확인 모달
  const getPrevMonthPayData = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `지난달 급여 불러오기`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `지난달 급여 불러오기`,
        desc: `지난달 작성된 급여가 있는 경우에만 데이터를\n불러오며 이후 저장하기 버튼을 눌러야\n저장됩니다.`,
        okBtnText: '확인',
        okCallback: () => fetchPrevMonthPayInfo(selectedEmpInfo?.EMP_SEQ, moment(date).subtract(1, 'month').toDate()),
      });
    }
  };

  // 급여명세서 초기화 확인 모달
  const resetPay = () => {
    if (!selectedEmpInfo) {
      setModalState({
        isModalShow: true,
        title: `급여명세서 초기화`,
        desc: `왼쪽 직원목록에서 직원을 선택해 주세요`,
      });
    } else {
      setModalState({
        isModalShow: true,
        title: `급여명세서 초기화`,
        desc: `${moment(date).format('YYYY년 MM월')} ${selectedEmpInfo.EMP_NAME}직원의\n 급여명세서를 초기화 합니다`,
        okBtnText: '확인',
        okCallback: () => handleResetPayInfo(payment.pay_type),
        isWarning: true,
      });
    }
  };

  // 월 변경 시 직원목록 패칭 요청
  useEffect(() => {
    fetchPaystubEmployeeList();
    if (selectedEmpInfo) {
      fetchPaystubPayInfo(selectedEmpInfo.EMP_SEQ, date);
    }
  }, [date, selectedEmpInfo]);

  useEffect(() => {
    if (sendHistoryData.modalShow === false) {
      setSendHistoryData({
        modalShow: false,
        memberIndex: 0,
        empName: '',
        data: [],
      });
    }
  }, [sendHistoryData.modalShow]);

  useEffect(() => {
    fetchFixedData();
  }, []);

  if (isPreview) {
    return (
      <div className="flex w-full justify-center">
        <div>
          <div className="mb-10 ml-auto flex w-[170px]">
            <Button label="급여명세서 작성하기" type="type2" height={38} action={() => setIsPreview(false)} />
          </div>{' '}
          <PaystubPreview deduction={deduction} payment={payment.payment_items} />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex flex-col ">
        <div className="flex">
          <div>
            <div className="flex">
              <div className="font-medium text-[#868686]">
                급여명세서 <span className="px-2.5 text-sm font-normal leading-5 text-[#c1c1c1]">{'>'}</span> 전송 및
                관리
              </div>
            </div>
            <div className="text-[28px] font-bold leading-[38px] text-[#333333]">급여명세서 전송 및 관리</div>

            <div className="mt-5">
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - step1. 왼쪽 지급 대상자 이름을 클릭하고 지급 내용 작성하세요.
              </div>
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - step2. 오른쪽 ‘계산하기’를 먼저 클릭하고 그 다음 ‘저장하기’를 클릭하세요.
              </div>
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - step3. 왼쪽 대상자를 체크하고 ‘카카오톡 발송’을 클릭하면 직원에게 급여명세서가 발송됩니다.
              </div>
            </div>
          </div>
          <div className="ml-auto flex flex-col items-end justify-between">
            <div className="flex">
              <div className="pr-2.5">
                <Button label="통계" type="type1" action={fetchAccumulatedStatisticsData} />
              </div>
              <div>
                <Button
                  label="직원 목록"
                  type="type2"
                  action={() => {
                    navigate('/paystub-employee-list');
                  }}
                />
              </div>
            </div>
            <div className="w-[210px]">
              <MonthInput onChange={handleCalendarInput} value={date} />
            </div>
          </div>
        </div>

        <div className="mt-[30px] flex">
          <div className="mr-[5px] h-[80vh] w-[45%] rounded-2xl bg-white p-[2%]">
            <div className="mb-5 flex">
              <div className="mr-1 text-[22px] font-bold leading-8 text-[#333333]">
                등록된 직원 <span className="text-[22px] font-bold leading-8 text-[#49ae9e]">{empList.length}</span>
              </div>
              <div className="ml-auto">
                <Button label="카카오톡 발송" type="type6" height={32} action={clickSendKakao} />
              </div>
            </div>
            <div className="ag-theme-quartz" style={{ height: 'calc(80vh - 100px)', width: '100%' }}>
              <AgGridReact
                rowData={empList}
                columnDefs={empColumns}
                defaultColDef={defaultColDef}
                rowSelection="multiple"
                suppressRowClickSelection={true}
                pagination={false}
                enableRangeSelection={false}
                suppressExcelExport={false}
                enableCharts={true}
                autoSizeStrategy={{ type: 'fitGridWidth' }}
                onSelectionChanged={(event) => {
                  const selectedRows = event.api.getSelectedRows();
                  setSelectedEmps(selectedRows);
                }}
                getRowStyle={(params) => {
                  if (params.data.EMP_SEQ === selectedEmpInfo?.EMP_SEQ) {
                    return { backgroundColor: '#f0f8f6' };
                  }
                  return undefined;
                }}
                overlayNoRowsTemplate={
                  '<span aria-live="polite" aria-atomic="true" style="padding: 10px;">등록된 직원 없음</span>'
                }
              />
            </div>
          </div>
          <div className="ml-[5px] w-[55%] rounded-2xl bg-white p-[2%]" id="SendStatementWrap">
            <div className="mb-5 flex">
              <div className="flex gap-1 text-[22px] font-bold leading-8 text-[#333333]">
                <span> 급여명세서 작성</span>
                <span className="text-[22px] font-bold leading-8 text-[#49ae9e]">
                  [{moment(date).format('YYYY년 MM월')} 급여 {selectedEmpInfo?.EMP_NAME ?? ''}]
                </span>
              </div>
            </div>
            <div className="mb-5 mt-2.5">
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - 지급항목을 입력 후 오른쪽 상단에 ‘계산하기’ 버튼을 클릭하면 공제액이 자동으로 계산돼요.
              </div>
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - 공제항목만 수정하는 경우 ‘저장하기’ 버튼을 반드시 클릭해주세요.
              </div>
              <div className="text-xs font-normal leading-[18px] text-[#999999]">
                - 계산하기 또는 저장하기를 클릭해야 총공제액, 총지급액, 실지급액이 합산돼요.
              </div>
            </div>
            <div className="mb-[44px] flex flex-wrap">
              <div className="mb-2.5 mr-[5px]">
                <Button
                  label="지난달 급여 불러오기"
                  type="type7"
                  icon="cloudIcon"
                  height={34}
                  action={getPrevMonthPayData}
                />
              </div>
              <div className="mb-2.5 mr-[5px]">
                <Button
                  label="출퇴근 기반 급여 불러오기"
                  type="type7"
                  icon="downloadIcon"
                  height={34}
                  action={confirmGetRealPayByCommute}
                />
              </div>
              <div className="mb-2.5 mr-[5px]">
                <Button label="이번달 급여초기화" type="type7" icon="RefreshIcon" height={34} action={resetPay} />
              </div>
              {isCalculated && (
                <div className="mb-2.5 mr-[5px]">
                  <Button
                    label="내역확인"
                    type="type7"
                    icon="RigthCircleIcon"
                    height={34}
                    action={() => {
                      setIsPreview(true);
                    }}
                  />
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-between">
              <div className="text-lg font-bold  text-[#333]">지급항목 작성</div>
              <div className="flex items-center gap-3">
                <div className="ml-auto mr-[5px] w-[120px]">
                  <Button
                    label="계산하기"
                    type="type2"
                    height={45}
                    action={() => {
                      handleCalculate();
                    }}
                  />
                </div>
                <div className="w-[120px]">
                  <Button label="저장하기" type="type14" height={45} action={() => savePaystub()} />
                </div>
              </div>
            </div>
            <div className="mt-5 flex items-center">
              {/* 시급인 경우만 노출 */}
              {payment.pay_type === '시급' ? (
                <div className="flex items-center">
                  <div>
                    <ToggleButton toggled={payment.pay2_flag} onClick={toggleUseSecondDefaultPay} disabled={false} />
                  </div>
                  <div>
                    <div className="ml-2.5 text-sm font-bold leading-5 text-[#505054]">기본급 2 사용</div>
                    <div className="ml-2.5 text-xs font-normal leading-[18px] text-[#999999]">
                      (시급이 변경되어 기본급이 2가지인 경우 설정, 기본급2 항목이 추가됩니다.)
                    </div>
                  </div>
                </div>
              ) : payment.pay_type === '일급' ? (
                <div>
                  <div className="ml-2.5 text-sm font-bold leading-5 text-[#505054]">일급 작성하기</div>
                  <div className="ml-2.5 text-xs font-normal leading-[18px] text-[#999999]">
                    총근무일과 총일급을 작성하세요.
                  </div>
                </div>
              ) : payment.pay_type === '월급' ? (
                <div>
                  <div className="ml-2.5 text-sm font-bold leading-5 text-[#505054]">월급 작성하기</div>
                  <div className="ml-2.5 text-xs font-normal leading-[18px] text-[#999999]">월급을 입력하세요.</div>
                </div>
              ) : null}
              <div className="ml-auto">
                <Select
                  className="text-sm font-normal"
                  components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: () => (
                      <div style={{ paddingRight: '15px' }}>
                        <ArrowDownIcon />
                      </div>
                    ),
                  }}
                  isSearchable={false}
                  value={payTypeOptions.find((option) => option.value === payment.pay_type) || payTypeOptions[0]}
                  styles={colorStyles}
                  options={payTypeOptions}
                  onChange={(newValue) => {
                    handlePayType(
                      newValue as SingleValue<{
                        value: string;
                        label: string;
                      }>,
                    );
                    handleResetPayInfo(newValue?.value || '시급');
                  }}
                />
              </div>
            </div>
            <PaystubPaymentTable payment={payment} setPayment={setPayment} pay2_flag={payment.pay2_flag} />
            <PaystubPaymentCustomTable payment={payment} setPayment={setPayment} pay2_flag={payment.pay2_flag} />
            <div className="mt-4 flex h-[54px] items-center justify-between rounded-lg bg-[#f2f2f2]">
              <div className="flex h-12 w-[440px] items-center justify-center border-r border-[#e1e1e1] p-1">
                <p className="text-base font-bold leading-5 text-[#505054]">총지급액</p>
              </div>
              <div className="flex h-12 w-[222px] items-center justify-end px-[60px] py-1">
                <p className="text-base font-bold leading-5 text-[#505054]">{addComma(deduction.total_pay || '0')}</p>
              </div>
            </div>
            {isCalculated && (
              <>
                <PaystubPaymentInfo deduction={deduction} />
                <PaystubDeductionTable deduction={deduction} setDeduction={setDeduction} previewInfo={previewInfo} />
                <PaystubDeductionCustomTable deduction={deduction} setDeduction={setDeduction} />
                <div className="mt-4 flex h-[54px] items-center justify-between rounded-lg bg-[#f2f2f2]">
                  <div className="flex h-12 w-[440px] items-center justify-center border-r border-[#e1e1e1] p-1">
                    <p className="text-base font-bold leading-5 text-[#505054]">총공제액</p>
                  </div>
                  <div className="flex h-12 w-[222px] items-center justify-end px-[60px] py-1">
                    <p className="text-base font-bold leading-5 text-[#505054]">
                      {addComma(
                        String(deduction.deduction_items.reduce((sum, item) => sum + (Number(item.pay_value) || 0), 0)),
                      )}
                      원
                    </p>
                  </div>
                </div>

                {/* 실지급액 */}
                <div className="mt-4 flex h-[72px] items-center justify-between rounded-lg bg-[#787878]">
                  <div className="flex h-12 w-[440px] items-center justify-center border-r border-[#e1e1e1] p-1">
                    <div className="flex flex-col items-center justify-end gap-1">
                      <div className="text-lg font-bold leading-5 text-white">실지급액</div>
                      <div className="text-sm leading-5 text-white">
                        총지급액({addComma(deduction.total_pay || '0')}) - 총공제액(
                        {addComma(
                          String(
                            deduction.deduction_items.reduce((sum, item) => sum + (Number(item.pay_value) || 0), 0),
                          ),
                        )}
                        원)
                      </div>
                    </div>
                  </div>
                  <div className="flex h-12 w-[222px] items-center justify-end px-[60px] py-1">
                    <p className="text-lg font-bold leading-5 text-white">
                      {addComma(
                        String(
                          Number(deduction.total_pay.replace(/[^0-9]/g, '') || 0) -
                            deduction.deduction_items.reduce((sum, item) => sum + (Number(item.pay_value) || 0), 0),
                        ),
                      )}
                      원
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <CommonModal isVisible={payStatistics.show} onClose={toggleStatisticModal} title={`전월 대비 급여 통계`}>
        <PaystubStatistics date={date} data={payStatistics} />
      </CommonModal>
      <CommonModal
        isVisible={sendHistoryData.modalShow}
        onClose={toggleSendHistoryModalVisible}
        title={`${sendHistoryData.empName}직원 급여명세서 전송이력`}>
        {sendHistoryData.data.length > 0 ? (
          <PaystubSendHistoryTable
            data={sendHistoryData.data}
            enterDetailPayPage={(id: string) => {
              enterDetailPayScreen(id);
            }}
          />
        ) : (
          <div className="flex h-20 w-full items-center justify-center">
            <div className="text-sm font-normal leading-5 text-[#999999]">급여명세서 전송이력이 없습니다.</div>
          </div>
        )}
      </CommonModal>
    </div>
  );
};

export default PaystubSendAndWrite;
